import { createContext, type ReactNode, useContext, useEffect, useState } from 'react';
import { BASE_PATH, ROUTES } from '@shared/constants';
import type { Route, RouterContextValue } from './types';

const RouterContext = createContext<RouterContextValue | null>(null);

interface RouterProviderProps {
  readonly children: ReactNode;
}

// Map paths to routes (all routes now include /myra base path)
function pathToRoute(path: string): Route {
  // Handle /myra/chat/thankyou subroute (must be checked before generic /myra/chat)
  if (path === ROUTES.CHAT_THANKYOU || path === `${ROUTES.CHAT_THANKYOU}/`) {
    return ROUTES.CHAT_THANKYOU;
  }
  // Handle dynamic chat routes like /myra/chat/:sessionId
  if (path === ROUTES.CHAT || path.startsWith(`${ROUTES.CHAT}/`)) {
    return ROUTES.CHAT;
  }
  // Handle /myra home route
  if (path === BASE_PATH || path === `${BASE_PATH}/`) {
    return ROUTES.HOME;
  }
  // Fallback: treat any other path as home (will redirect via nginx)
  return ROUTES.HOME;
}

// Map routes to paths with query params
function routeToPath(route: Route, queryParams?: Record<string, string>): string {
  if (!queryParams || Object.keys(queryParams).length === 0) {
    return route;
  }
  const params = new URLSearchParams(queryParams);
  return `${route}?${params.toString()}`;
}

export function RouterProvider({ children }: RouterProviderProps) {
  const [currentRoute, setCurrentRoute] = useState<Route>(() => {
    // Read from URL on mount
    const path = window.location.pathname;
    return pathToRoute(path);
  });

  // Navigate to a new route
  const navigate = (route: Route, queryParams?: Record<string, string>) => {
    setCurrentRoute(route);
    const path = routeToPath(route, queryParams);
    window.history.pushState({}, '', path);
  };

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname;
      setCurrentRoute(pathToRoute(path));
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  return (
    <RouterContext.Provider value={{ currentRoute, navigate }}>{children}</RouterContext.Provider>
  );
}

export function useRouter(): RouterContextValue {
  const context = useContext(RouterContext);
  if (!context) {
    throw new Error('useRouter must be used within RouterProvider');
  }
  return context;
}
